import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import ChatModal from '../../components/feature/ChatModal';
import { findProviderById } from '../../mocks/allProviders';

export default function ProviderProfilePage() {
  const { id } = useParams();
  const provider = findProviderById(Number(id));
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    if (!provider) return;
    document.title = `${provider.name} – ${provider.category} en Aguascalientes | Task Zone`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `Contrata a ${provider.name}, proveedor verificado de ${provider.category} en Aguascalientes. Calificación ${provider.rating}/5 con ${provider.reviewCount} reseñas. Precios en MXN. Solicita tu servicio en Task Zone.`);

    const schemaId = 'schema-provider-profile';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: `${provider.category} – ${provider.name}`,
      description: provider.description,
      provider: {
        '@type': 'Person',
        name: provider.name,
        image: provider.avatar ?? provider.image,
        jobTitle: provider.category,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Aguascalientes',
          addressRegion: 'AGU',
          addressCountry: 'MX',
        },
      },
      areaServed: {
        '@type': 'City',
        name: 'Aguascalientes',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: provider.rating,
        reviewCount: provider.reviewCount,
        bestRating: 5,
        worstRating: 1,
      },
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/proveedor/${provider.id}`,
      priceCurrency: 'MXN',
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [provider]);

  if (!provider) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-50 rounded-full mx-auto mb-4">
              <i className="ri-user-unfollow-line text-3xl text-orange-400"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Proveedor no encontrado</h2>
            <p className="text-gray-500 mb-4">El proveedor que buscas no existe o ya no está disponible.</p>
            <Link to="/explorar">
              <Button>Explorar proveedores</Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const reviews = [
    {
      id: 1,
      userName: 'María López',
      rating: 5,
      date: '15 Ene 2024',
      comment: 'Excelente servicio, muy profesional y puntual. Totalmente recomendado.'
    },
    {
      id: 2,
      userName: 'Carlos Ruiz',
      rating: 5,
      date: '12 Ene 2024',
      comment: 'Trabajo impecable, quedé muy satisfecho con el resultado.'
    },
    {
      id: 3,
      userName: 'Ana Martínez',
      rating: 4,
      date: '08 Ene 2024',
      comment: 'Buen servicio, aunque tardó un poco más de lo esperado.'
    }
  ];

  const displayImage = provider.images?.[0] ?? provider.image;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="flex-1 bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/explorar" className="inline-flex items-center text-orange-600 hover:text-orange-700 mb-6 cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Volver a Explorar
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-md p-8 mb-6">
                {/* Work images carousel if premium */}
                {provider.images && provider.images.length > 1 && (
                  <div className="mb-6 rounded-xl overflow-hidden w-full h-56">
                    <img
                      src={provider.images[0]}
                      alt={`Trabajo de ${provider.name}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                )}

                <div className="flex flex-col md:flex-row gap-6 mb-6">
                  <div className="w-48 h-48 flex-shrink-0">
                    <img
                      src={provider.avatar ?? provider.image}
                      alt={provider.name}
                      className="w-full h-full object-cover object-top rounded-xl"
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h1 className="text-3xl font-bold text-gray-900">{provider.name}</h1>
                          {provider.badge && (
                            <span className="px-2.5 py-0.5 text-xs font-bold rounded-full text-white"
                              style={{ background: 'linear-gradient(135deg, #F57C00, #FF9800)' }}>
                              <i className="ri-medal-fill mr-1"></i>{provider.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-lg text-orange-600 font-medium mb-2">{provider.category}</p>
                      </div>
                      {provider.available ? (
                        <span className="px-4 py-2 bg-green-100 text-green-700 text-sm font-semibold rounded-lg whitespace-nowrap">
                          <i className="ri-checkbox-circle-fill mr-1"></i>
                          Disponible
                        </span>
                      ) : (
                        <span className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg whitespace-nowrap">
                          <i className="ri-close-circle-fill mr-1"></i>
                          No Disponible
                        </span>
                      )}
                    </div>

                    <div className="flex items-center space-x-6 mb-4">
                      <div className="flex items-center">
                        <i className="ri-star-fill text-yellow-400 text-xl mr-1"></i>
                        <span className="text-xl font-bold text-gray-900">{provider.rating}</span>
                        <span className="text-gray-500 ml-1">({provider.reviewCount} reseñas)</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <i className="ri-briefcase-line mr-2"></i>
                        <span>{provider.completedJobs} trabajos completados</span>
                      </div>
                    </div>

                    <div className="flex items-center text-gray-600 mb-4">
                      <i className="ri-time-line mr-2"></i>
                      <span>Tiempo de respuesta: {provider.responseTime}</span>
                    </div>

                    {provider.serviceArea && (
                      <div className="flex items-center text-gray-600 mb-4">
                        <i className="ri-map-pin-line mr-2 text-orange-500"></i>
                        <span className="text-sm">{provider.serviceArea}</span>
                      </div>
                    )}

                    {provider.skills && provider.skills.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {provider.skills.map((skill, index) => (
                          <span key={index} className="px-3 py-1 bg-orange-50 text-orange-700 text-sm font-medium rounded-full">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3">Sobre el Proveedor</h2>
                  <p className="text-gray-700 leading-relaxed">{provider.description}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Reseñas de Clientes</h2>
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold text-gray-900">{review.userName}</p>
                          <p className="text-sm text-gray-500">{review.date}</p>
                        </div>
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`${
                                i < review.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'
                              }`}
                            ></i>
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-700">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
                <div className="text-center mb-6 pb-6 border-b border-gray-200">
                  <p className="text-sm text-gray-500 mb-1">Precio desde</p>
                  {provider.priceRange ? (
                    <p className="text-2xl font-bold text-orange-600">{provider.priceRange}</p>
                  ) : (
                    <>
                      <p className="text-4xl font-bold text-gray-900">${provider.basePrice}</p>
                      <p className="text-gray-500 text-sm">MXN / hora</p>
                    </>
                  )}
                </div>

                {provider.available ? (
                  <Link to={`/solicitar-servicio/${provider.id}`}>
                    <Button fullWidth size="lg">
                      <i className="ri-calendar-check-line mr-2"></i>
                      Solicitar Servicio
                    </Button>
                  </Link>
                ) : (
                  <Button fullWidth size="lg" disabled>
                    <i className="ri-close-circle-line mr-2"></i>
                    No Disponible
                  </Button>
                )}

                <button
                  onClick={() => setShowChat(true)}
                  className="w-full mt-3 flex items-center justify-center gap-2 py-3 px-4 border-2 border-orange-500 text-orange-600 rounded-xl font-semibold text-sm hover:bg-orange-50 transition cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-chat-3-line text-lg"></i>
                  Chatear con proveedor
                </button>

                <div className="mt-6 space-y-3">
                  <div className="flex items-center text-gray-600">
                    <i className="ri-shield-check-line text-orange-600 text-xl mr-3"></i>
                    <span className="text-sm">Proveedor verificado</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <i className="ri-customer-service-line text-orange-600 text-xl mr-3"></i>
                    <span className="text-sm">Soporte 24/7</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <i className="ri-money-dollar-circle-line text-orange-600 text-xl mr-3"></i>
                    <span className="text-sm">Precios en MXN</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {showChat && (
        <ChatModal
          providerName={provider.name}
          providerImage={provider.avatar ?? provider.image}
          providerCategory={provider.category}
          onClose={() => setShowChat(false)}
        />
      )}
    </div>
  );
}